import { useState } from 'react';
import { api, ApiError } from '../api';
import { fieldVisible, type Me } from '../auth/me';
import { trapTab } from '../ui/focus';
import { blockRights } from './InsertBlockDialog';
import { LINES_RESOURCE, type Version } from './types';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type Kind = 'quote' | 'equipment';

/**
 * "Zapisz sekcję jako blok" (F26): a section of the shown version (any state: saving changes nothing in the quote) as
 * a block of the quote ("Bloki kosztorysów": every line, prices kept or taken from the price list at insertion) or a
 * block of equipment (F13: the equipment only, for packlists and templates too).
 */
export function SaveBlockDialog({ me, version, onDone, onClose }: { me: Me; version: Version; onDone: (message: string) => void; onClose: () => void }) {
  const rights = blockRights(me);
  const sections = version.sections.filter((s) => s.lines.length > 0);
  const [sectionId, setSectionId] = useState(sections[0]?.id ?? '');
  const section = sections.find((s) => s.id === sectionId) ?? null;
  const [name, setName] = useState(section?.name ?? '');
  const [kind, setKind] = useState<Kind>(rights.saveQuote ? 'quote' : 'equipment');
  const seesPrices = fieldVisible(me, LINES_RESOURCE, 'unitPriceGrosze');
  // F26 review: off by default — prices go into a block only on purpose
  const [keepPrices, setKeepPrices] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(): Promise<boolean> {
    if (!section || name.trim() === '') return false;
    setBusy(true);
    setError('');
    try {
      if (kind === 'quote') {
        await api(`/api/quotes/sections/${section.id}/save-as-quote-block`, { method: 'POST', body: { name, keepPrices: keepPrices && seesPrices } });
        onDone(`Zapisano blok kosztorysu „${name.trim()}” (${section.lines.length} poz.). Wstawisz go przyciskiem „Wstaw blok”.`);
      } else {
        const r = await api<{ blockId: string; skipped: string[] }>(`/api/quotes/sections/${section.id}/save-as-block`, { method: 'POST', body: { name } });
        onDone(`Zapisano blok sprzętu „${name.trim()}”${r.skipped.length > 0 ? ` (bez ${r.skipped.length} poz. spoza katalogu sprzętu)` : ''}.`);
      }
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się zapisać bloku');
      setBusy(false);
      return false;
    }
  }
  // owner 2026-10-23 21:14: Escape or leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening({ sectionId, name, kind, keepPrices });
  const guardRef = useUnsavedGuard({ dirty: !sameValues({ sectionId, name, kind, keepPrices }, opening), save });

  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-block-title"
        data-feature-id="quotes.blocks"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="save-block-title">Zapisz sekcję jako blok</h2>
        {sections.length === 0 ? (
          <p className="muted">Ta wersja nie ma sekcji z pozycjami.</p>
        ) : (
          <>
            <label className="field">
              <span>Sekcja</span>
              <select
                value={sectionId}
                onChange={(e) => {
                  setSectionId(e.target.value);
                  setName(sections.find((s) => s.id === e.target.value)?.name ?? '');
                }}
              >
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.lines.length} poz.)
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Nazwa bloku</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
            </label>
            <fieldset className="field">
              <legend>Rodzaj</legend>
              {rights.saveQuote && (
                <label className="check">
                  <input type="radio" name="block-kind" checked={kind === 'quote'} onChange={() => setKind('quote')} />
                  <span>Blok kosztorysu — wszystkie pozycje (także usługi i pozycje własne)</span>
                </label>
              )}
              {rights.saveEquipment && (
                <label className="check">
                  <input type="radio" name="block-kind" checked={kind === 'equipment'} onChange={() => setKind('equipment')} />
                  <span>Blok sprzętu — tylko sprzęt z katalogu (do sprzętówek i szablonów)</span>
                </label>
              )}
            </fieldset>
            {kind === 'quote' && seesPrices && (
              <label className="check">
                <input type="checkbox" checked={keepPrices} onChange={(e) => setKeepPrices(e.target.checked)} />
                <span>Zapisz ceny z tej sekcji (bez zaznaczenia przy wstawieniu ceny z cennika)</span>
              </label>
            )}
            {kind === 'quote' && (
              <p className="hint">Blok zobaczą osoby z prawem do bloków kosztorysów.</p>
            )}
          </>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={!section || busy || name.trim() === ''}>
            {busy ? 'Zapisywanie…' : 'Zapisz blok'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
